import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class DateService {
  constructor() {}

  isToday(date: Date) {
    const today = new Date();
    return (
      date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate()
    );
  }

  isYesterday(date: Date): boolean {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);

    return (
      date.getFullYear() === yesterday.getFullYear() &&
      date.getMonth() === yesterday.getMonth() &&
      date.getDate() === yesterday.getDate()
    );
  }
}

// Serbian month names are lowercase inside a date ("12. okt 2026"); helpers that start
// a phrase with the month capitalize it. The day is an ordinal, so it always takes a dot.
const SR_MONTHS = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'avg', 'sep', 'okt', 'nov', 'dec'];

const SR_MONTHS_FULL = [
  'januar', 'februar', 'mart', 'april', 'maj', 'jun',
  'jul', 'avgust', 'septembar', 'oktobar', 'novembar', 'decembar',
];

const SR_WEEKDAYS_FULL = [
  'Nedelja', 'Ponedeljak', 'Utorak', 'Sreda', 'Četvrtak', 'Petak', 'Subota',
];

const SR_WEEKDAYS_SHORT = ['Ned', 'Pon', 'Uto', 'Sre', 'Čet', 'Pet', 'Sub'];

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function timeHhMm(date: Date): string {
  return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

/**
 * Formats Date in Serbian like 'Petak, 10. januar 2025.'
 */
export function getDateFormatedSrLong(date: Date): string {
  return `${SR_WEEKDAYS_FULL[date.getDay()]}, ${getDateFormatedSrFullMonth(date)}.`;
}

/**
 * Formats Date in Serbian like 'Pet, 10. januar 2025'
 */
export function getDateFormatedSrShortWeekday(date: Date): string {
  return `${SR_WEEKDAYS_SHORT[date.getDay()]}, ${getDateFormatedSrFullMonth(date)}`;
}

/**
 * Formats Date in Serbian like '15. maj 2025'
 */
export function getDateFormatedSr(date: Date): string {
  return `${date.getDate()}. ${SR_MONTHS[date.getMonth()]} ${date.getFullYear()}`;
}

/**
 * Formats Date in Serbian like '15. maj'
 */
export function getDayMonthSr(date: Date): string {
  return `${date.getDate()}. ${SR_MONTHS[date.getMonth()]}`;
}

/**
 * Formats Date in Serbian like '15. oktobar'
 */
export function getDayFullMonthSr(date: Date): string {
  return `${date.getDate()}. ${SR_MONTHS_FULL[date.getMonth()]}`;
}

/**
 * Formats Date in Serbian like 'Septembar 2026'
 */
export function getMonthYearSr(date: Date): string {
  return `${capitalize(SR_MONTHS_FULL[date.getMonth()])} ${date.getFullYear()}`;
}

/**
 * Formats Date in Serbian like 'SEP'
 */
export function getMonthShortSr(date: Date): string {
  return SR_MONTHS[date.getMonth()].toUpperCase();
}

/**
 * Formats Date in Serbian like '11. januar 2025'
 */
export function getDateFormatedSrFullMonth(date: Date): string {
  return `${date.getDate()}. ${SR_MONTHS_FULL[date.getMonth()]} ${date.getFullYear()}`;
}

/**
 * Formats Date in Serbian like '11. januar 2025 • 14:32'
 */
export function getDateFormatedSrFullMonthWithTime(date: Date): string {
  return `${getDateFormatedSrFullMonth(date)} • ${timeHhMm(date)}`;
}

/**
 * Formats Date in Serbian like '15. maj 2025 • 14:55'
 */
export function getDateFormatedSrWithTime(date: Date): string {
  return `${getDateFormatedSr(date)} • ${timeHhMm(date)}`;
}

/**
 * Formats Date in Serbian like '7. dec 2019 • 23:26'
 */
export function getDateFormatedWithTime(date: Date) {
  return getDateFormatedSrWithTime(date);
}

/**
 * Formats Date in Serbian like '8. dec 2025, 14:32'
 */
export function getDateFormattedShort(date: Date) {
  return `${getDateFormatedSr(date)}, ${timeHhMm(date)}`;
}

/**
 * Formats Date like 'Januray 1, 2024'
 */
export function getDateFormated(date: Date) {
  const formatter = new Intl.DateTimeFormat('en-GB', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });

  const parts = formatter.formatToParts(date);

  return parts
    .map((part) => {
      if (part.type === 'weekday') {
        return part.value + ',';
      }
      return part.value;
    })
    .join('');
}

/**
 * Formats Date like '1 January 2024, 10:00 ' 'Januray 1, 2024'
 */
export function getDateFormatedDdMonthYyyyHhMM(date: Date) {
  const formatter = new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  return formatter.format(date);
}

export function convertDateToHhMm(date: Date) {
  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

export function convertDateToHhMmSs(date: Date) {
  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(date);
}

export function convertStrToHHMM(timeStr: string): string {
  const parts = timeStr.split(':');
  if (parts.length < 2) {
    return timeStr;
  }
  return parts.slice(0, 2).join(':');
}

export function isLessThan24hAway(date: Date) {
  const now = new Date();
  const diffInMs = date.getTime() - now.getTime();

  const hoursDiff = diffInMs / (1000 * 60 * 60); // ms → hours

  return hoursDiff < 24;
}

export function isInFuture(date: Date) {
  return date > new Date();
}

function pluralizeSr(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

export function getRelativeTimeSr(date: Date): string {
  const diffSec = Math.floor((Date.now() - date.getTime()) / 1000);

  if (diffSec < 60) {
    return 'Upravo sada';
  }

  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) {
    return `Pre ${diffMin} ${pluralizeSr(diffMin, 'minut', 'minuta', 'minuta')}`;
  }

  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) {
    return `Pre ${diffHours} ${pluralizeSr(diffHours, 'sat', 'sata', 'sati')}`;
  }

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) {
    return `Pre ${diffDays} ${pluralizeSr(diffDays, 'dan', 'dana', 'dana')}`;
  }

  return getDateFormatedSr(date);
}
